import JustValidate from 'just-validate';
import axios from 'axios';
import ContactModal from './modal';
import ConsentSolution from '../../gdpr/consent-solution';
import getCity from '../core/select-city';

// Posthog
import ph from '../../analytics/posthog/posthog-client';

declare let contact_object: any;

/**
 * Handles contact form events including validation, submission, and response handling.
 */
export default class ContactEvent{
    
    private readonly apiUrl: string = contact_object.rest_urls.contact_event;
    private readonly spinnerClass: string = 'bgdrop-spin';
    private form: HTMLFormElement | null;
    private button: HTMLButtonElement | null;
    private citySelector: any = null;
    private consentSolution = new ConsentSolution();
    private modal = new ContactModal();

    /**
     * Initializes the contact event instance.
     * @param formSelector - Selector for the form element
     * @param buttonSelector - Selector for the submit button
     */
    constructor(private formSelector: string, private buttonSelector: string){
        
        this.form = document.querySelector<HTMLFormElement>(formSelector);
        this.button = document.querySelector<HTMLButtonElement>(buttonSelector);
        this.init();

    }

    /**
     * Initializes form validation and city selection.
     */
    private init(){
        
        if(!this.form) return;
        this.citySelector = getCity('#field_city');
        this.setupValidation();

    }

    /**
     * Sets up form validation rules using JustValidate.
     */
    private setupValidation() {
        
        const validation = new JustValidate(this.formSelector, {
            errorFieldCssClass: 'is-invalid bg-red-050 border border-red-700 focus:border-red-700 focus:ring-red-700 placeholder:text-gray-900',
            errorLabelCssClass: 'just-validate-error-label flex flex-row flex-wrap basis-full w-full mt-1 fluid-text-sm text-red-700',
            focusInvalidField: true,
            lockForm: true,
        });

        this.addValidationRules(validation);

    }

    /**
     * Adds validation rules for the form fields.
     */
    private addValidationRules(validation: JustValidate){

        validation
        .addField('#field_name', [
            { rule: 'required', errorMessage: contact_object.error_messages.name_required },
            { rule: 'minLength', value: 3 },
        ])
        .addField('#field_surname', [
            { rule: 'required', errorMessage: contact_object.error_messages.name_required },
            { rule: 'minLength', value: 3 },
        ])
        .addField('#field_email', [
            { rule: 'required', errorMessage: contact_object.error_messages.email_required },
            { rule: 'email', errorMessage: contact_object.error_messages.email_invalid },
        ])
        .addField('#field_phone', [
            { rule: 'required', errorMessage: contact_object.error_messages.telephone_required },
        ])
        .addField('#field_city', [
            { rule: 'required', errorMessage: contact_object.error_messages.city_required },
        ])
        .addField('#field_message', [
            { rule: 'required', errorMessage: contact_object.error_messages.message_required },
            { rule: 'minLength', value: 10, errorMessage: contact_object.error_messages.message_short },
        ])
        .addField('#field_privacy_policy', [
            { rule: 'required', errorMessage: contact_object.error_messages.check_privacy_policy },
        ])
        .onSuccess((event) => {
            event?.preventDefault();
            this.submitForm();
        });

    }

    /**
     * Handles form submission.
     */
    private async submitForm(){
        
        if(!this.form || !this.button) return;

        const formData = new FormData(this.form);
        const cityLabel = (document.querySelector<HTMLSelectElement>('#field_city')?.selectedOptions[0]?.text) || '';
        const posthogData = this.collectPosthogData(formData, cityLabel);
        const queryString = new URLSearchParams({ fields: new URLSearchParams(formData as any).toString() }).toString();

        this.button.disabled = true;
        const spinner = this.createSpinner();
        this.button.appendChild(spinner);

        try{
            
            const response = await axios.post(this.apiUrl, queryString, { validateStatus: (status) => status === 200 });
            document.body.insertAdjacentHTML('beforeend', response.data.message);
            if(response.data.status) this.handleSuccess(posthogData);

        }catch (error: any){
            
            this.handleError(error);

        }finally{

            spinner.remove();
            this.button.disabled = false;

        }

    }

    /**
     * Collects user input data for analytics.
     */
    private collectPosthogData(formData: FormData, cityLabel: string){
        
        return {
            name: formData.get('field_name') as string,
            surname: formData.get('field_surname') as string,
            email: formData.get('field_email') as string,
            city: cityLabel,
            phone: formData.get('phone_full') as string,
            message: formData.get('field_message') as string,
        };

    }

    /**
     * Handles a successful form submission.
     */
    private handleSuccess(posthogData: any){
        
        this.consentSolution.getConsent(this.formSelector);
        ph.captureContactForm('[Contact] [B2B] Events organization', 'B2B', posthogData.name, posthogData.surname, posthogData.email, posthogData.city, posthogData.phone, posthogData.message);
        this.modal.get('successModal', 'closeSuccessModal');
        this.citySelector.clear(true);
        this.citySelector.clearOptions();
        this.form?.reset();

    }

    /**
     * Handles errors during form submission.
     */
    private handleError(error: any){

        if(error.response?.status === 201){

            document.body.insertAdjacentHTML('beforeend', error.response.data.message);
            this.modal.get('errorModal', 'closeErrorModal');

        }

    }

    /**
     * Creates a loading spinner element.
     */
    private createSpinner(): HTMLElement{

        const spinner = document.createElement('i');
        spinner.id = this.spinnerClass;
        spinner.className = 'fa-solid fa-circle-notch fa-spin ml-1 mt-1';
        return spinner;

    }
    
}
